import { useState, useEffect } from "react";
import { SANS, TAP, color, font, type, weight } from "./theme";
import { Icon, Pressable, SectionLabel, Toggle as Switch } from "./ui";
import { supabase } from "./supabase";

function Toggle({ label, sublabel, value, onChange, disabled }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: color.surface, border: `1px solid ${color.lineStrong}`, borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
      <div>
        <div style={{ fontSize: 14, color: color.heading }}>{label}</div>
        {sublabel && <div style={{ fontSize: type.xs, color: color.dim, marginTop: 2 }}>{sublabel}</div>}
      </div>
      <Switch checked={value} onChange={onChange} disabled={disabled} label={label} />
    </div>
  );
}

// A labelled group of rows separated by hairlines, rather than a card with a
// heavy border. The wall of identical outlined buttons was the main reason
// Settings read as a game menu.
function Section({ id, title, openSection, onToggle, children }) {
  const open = openSection === id;
  return (
    <div style={{ marginBottom: 4 }}>
      <Pressable
        onClick={() => onToggle(id)}
        label={title}
        style={{
          width: "100%", display: "flex", justifyContent: "space-between",
          alignItems: "center", borderBottom: `1px solid ${color.border}`,
        }}
      >
        <span style={{ fontFamily: font.display, fontSize: type.md, fontWeight: weight.displayMed, color: color.textPrimary }}>
          {title}
        </span>
        <span style={{ display: "flex", transform: open ? "rotate(90deg)" : "none", transition: "transform 160ms" }}>
          <Icon.Chevron size={15} color={color.textFaint} />
        </span>
      </Pressable>
      {open && (
        <div style={{ padding: "14px 0 18px" }}>
          {children}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, sub }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, padding: "12px 0", borderBottom: `1px solid ${color.border}` }}>
      <div>
        <div style={{ fontFamily: font.display, fontSize: type.md, fontWeight: weight.displayMed, color: color.textPrimary }}>{label}</div>
        {sub && <div style={{ fontSize: type.sm, color: color.textFaint, marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ fontSize: type.sm, color: color.textMuted, textAlign: "right", maxWidth: "55%", lineHeight: 1.45 }}>{value}</div>
    </div>
  );
}

function Term({ word, def }) {
  return (
    <div style={{ padding: "12px 0", borderBottom: `1px solid ${color.border}` }}>
      <div style={{ fontFamily: font.display, fontSize: type.md, fontWeight: weight.displayMed, color: color.textPrimary, marginBottom: 3 }}>{word}</div>
      <div style={{ fontSize: type.sm, color: color.textMuted, lineHeight: 1.5 }}>{def}</div>
    </div>
  );
}

export default function Settings({ user, onClose, onSignOut, onReplayTour }) {
  const [section, setSection] = useState("account");

  const displayName = user?.user_metadata?.display_name || "";
  const username = user?.user_metadata?.username?.replace(/^@/, "") || "";

  return (
    <div style={{ position: "fixed", inset: 0, background: color.bg, zIndex: 600, display: "flex", flexDirection: "column", fontFamily: SANS, maxWidth: 420, margin: "0 auto", left: "50%", transform: "translateX(-50%)", width: "100%" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 16px 12px", borderBottom: `1px solid ${color.lineStrong}` }}>
        <button onClick={onClose}
          style={{ background: "none", border: "none", color: color.tan, fontSize: 20, cursor: "pointer", padding: 4, lineHeight: 1 }}>
          ←
        </button>
        <div style={{ fontSize: 16, fontWeight: 700, color: color.heading }}>Settings</div>
      </div>

      {/* Section tabs */}
      <div style={{ display: "flex", borderBottom: `1px solid ${color.lineStrong}` }}>
        {[["account", "Account"], ["privacy", "Privacy"], ["guide", "Guide"], ["help", "Help"]].map(([id, label]) => (
          <button key={id} onClick={() => setSection(id)}
            style={{ flex: 1, background: "none", border: "none", borderBottom: `2px solid ${section === id ? color.goldLegacy : "transparent"}`, padding: "10px 0", color: section === id ? color.goldLegacy : color.dim, fontSize: 13, cursor: "pointer", fontFamily: SANS, fontWeight: section === id ? 700 : 400, textAlign: "center" }}>
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {section === "account" && <AccountSection user={user} displayName={displayName} username={username} onSignOut={onSignOut} />}
        {section === "privacy" && <PrivacySection user={user} />}
        {section === "guide"   && <GuideSection />}
        {section === "help"    && <HelpSection onReplayTour={onReplayTour} user={user} />}
      </div>
    </div>
  );
}

function Field({ label, value, onSave, type = "text", hint }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    const result = await onSave(val.trim());
    setSaving(false);
    if (result?.error) {
      setMsg({ text: result.error, isError: true });
    } else {
      setMsg({ text: "Saved!", isError: false });
      setEditing(false);
    }
    setTimeout(() => setMsg(null), 3000);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: type.xs, color: color.dim, letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      {editing ? (
        <div>
          <input
            type={type}
            value={val}
            onChange={e => setVal(e.target.value)}
            style={{ width: "100%", background: color.surfaceRaised, border: `1px solid ${color.goldLegacy}`, borderRadius: 8, padding: "10px 12px", color: color.heading, fontSize: type.md, fontFamily: SANS, outline: "none", boxSizing: "border-box" }}
            autoFocus
          />
          {hint && <div style={{ fontSize: type.xs, color: color.dim, marginTop: 4 }}>{hint}</div>}
          {msg && <div style={{ fontSize: type.xs, color: msg.isError ? color.dangerText : color.green, marginTop: 4 }}>{msg.text}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button onClick={handleSave} disabled={saving}
              style={{ flex: 1, background: saving ? color.line : color.gold, border: "none", borderRadius: 8, padding: "9px 0", color: saving ? color.dim : color.bg, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: SANS }}>
              {saving ? "Saving..." : "Save"}
            </button>
            <button onClick={() => { setEditing(false); setVal(value || ""); }}
              style={{ flex: 1, background: "none", border: `1px solid ${color.lineStrong}`, borderRadius: 8, padding: "9px 0", color: color.dim, fontSize: 13, cursor: "pointer", fontFamily: SANS }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: color.surface, border: `1px solid ${color.lineStrong}`, borderRadius: 8, padding: "10px 12px" }}>
          <span style={{ fontSize: 14, color: value ? color.heading : color.faint }}>{value || "Not set"}</span>
          <button onClick={() => setEditing(true)}
            style={{ background: "none", border: "none", color: color.goldLegacy, fontSize: type.xs, cursor: "pointer", fontFamily: SANS }}>
            Edit
          </button>
        </div>
      )}
    </div>
  );
}

function AccountSection({ user, displayName, username, onSignOut }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [msg, setMsg] = useState(null);

  const saveDisplayName = async (val) => {
    if (!val) return { error: "Display name cannot be empty." };
    const { error } = await supabase.auth.updateUser({ data: { display_name: val } });
    if (!error) await supabase.from("users").update({ display_name: val }).eq("id", user.id);
    return error ? { error: error.message } : {};
  };

  const saveEmail = async (val) => {
    if (!val) return { error: "Email cannot be empty." };
    const { error } = await supabase.auth.updateUser({ email: val });
    return error ? { error: error.message } : {};
  };

  const handlePasswordReset = async () => {
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: "https://ashed.app",
    });
    if (error) {
      setMsg({ text: "Error sending reset email.", isError: true });
    } else {
      setMsg({ text: "Password reset email sent to " + user.email, isError: false });
    }
    setTimeout(() => setMsg(null), 5000);
  };

  const handleDeleteAccount = async () => {
    if (deleteInput !== "DELETE") return;
    // Sign out first, then delete via service key would be needed server-side
    // For now, flag the account for deletion and sign out
    await supabase.from("users").update({ flagged_for_deletion: true }).eq("id", user.id);
    await supabase.auth.signOut();
    onSignOut();
  };

  return (
    <div>
      <div style={{ fontSize: type.xs, color: color.dim, letterSpacing: 1, marginBottom: 16 }}>PROFILE</div>
      <Field label="DISPLAY NAME" value={displayName} onSave={saveDisplayName} />
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: type.xs, color: color.dim, letterSpacing: 1, marginBottom: 4 }}>USERNAME</div>
        <div style={{ background: color.surface, border: `1px solid ${color.lineStrong}`, borderRadius: 8, padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, color: color.faint }}>{username ? `@${username}` : "Not set"}</span>
          <span style={{ fontSize: type.xs, color: color.faint }}>Cannot be changed</span>
        </div>
      </div>
      
      <div style={{ fontSize: type.xs, color: color.dim, letterSpacing: 1, marginBottom: 16, marginTop: 24 }}>SECURITY</div>
      <Field label="EMAIL ADDRESS" value={user?.email} onSave={saveEmail} hint="A confirmation will be sent to your current email address." />

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: type.xs, color: color.dim, letterSpacing: 1, marginBottom: 4 }}>PASSWORD</div>
        <div style={{ background: color.surface, border: `1px solid ${color.lineStrong}`, borderRadius: 8, padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, color: color.faint }}>••••••••</span>
          <button onClick={handlePasswordReset}
            style={{ background: "none", border: "none", color: color.goldLegacy, fontSize: type.xs, cursor: "pointer", fontFamily: SANS }}>
            Reset via Email
          </button>
        </div>
        {msg && <div style={{ fontSize: type.xs, color: msg.isError ? color.dangerText : color.green, marginTop: 6 }}>{msg.text}</div>}
      </div>

      <div style={{ fontSize: type.xs, color: color.dim, letterSpacing: 1, marginBottom: 16, marginTop: 24 }}>ACCOUNT ACTIONS</div>
      <button onClick={onSignOut}
        style={{ width: "100%", background: "none", border: `1px solid ${color.lineStrong}`, borderRadius: 10, padding: 14, color: color.tan, fontSize: 14, cursor: "pointer", fontFamily: SANS, marginBottom: 10 }}>
        Sign Out
      </button>

      {!confirmDelete ? (
        <button onClick={() => setConfirmDelete(true)}
          style={{ width: "100%", background: "none", border: `1px solid ${color.danger}44`, borderRadius: 10, padding: 14, color: color.danger, fontSize: 14, cursor: "pointer", fontFamily: SANS }}>
          Delete Account
        </button>
      ) : (
        <div style={{ background: color.surface, border: `1px solid ${color.danger}44`, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 13, color: color.dangerText, marginBottom: 12, lineHeight: 1.6 }}>
            This will permanently delete your account and all your data. Type <strong>DELETE</strong> to confirm.
          </div>
          <input value={deleteInput} onChange={e => setDeleteInput(e.target.value)}
            placeholder="Type DELETE"
            style={{ width: "100%", background: color.bg, border: `1px solid ${color.danger}`, borderRadius: 8, padding: "10px 12px", color: color.heading, fontSize: type.md, fontFamily: SANS, outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleDeleteAccount} disabled={deleteInput !== "DELETE"}
              style={{ flex: 1, background: deleteInput === "DELETE" ? color.danger : color.line, border: "none", borderRadius: 8, padding: 10, color: deleteInput === "DELETE" ? color.heading : color.faint, fontSize: 13, fontWeight: 700, cursor: deleteInput === "DELETE" ? "pointer" : "default", fontFamily: SANS }}>
              Delete Forever
            </button>
            <button onClick={() => { setConfirmDelete(false); setDeleteInput(""); }}
              style={{ flex: 1, background: "none", border: `1px solid ${color.lineStrong}`, borderRadius: 8, padding: 10, color: color.dim, fontSize: 13, cursor: "pointer", fontFamily: SANS }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PrivacySection({ user }) {
  const [privateProfile, setPrivateProfile] = useState(false);
  const [privateCheckins, setPrivateCheckins] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase.from("users").select("private_profile, default_private_checkins").eq("id", user.id).single()
      .then(({ data }) => {
        if (cancelled) return;
        if (data) {
          setPrivateProfile(data.private_profile || false);
          setPrivateCheckins(data.default_private_checkins || false);
        }
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (field, val, setter) => {
    setSaving(true);
    setter(val);
    await supabase.from("users").update({ [field]: val }).eq("id", user.id);
    setSaving(false);
  };

  return (
    <div>
      <div style={{ fontSize: type.xs, color: color.dim, letterSpacing: 1, marginBottom: 16 }}>PRIVACY</div>
      {!loaded ? (
        <div style={{ fontSize: 13, color: color.faint, textAlign: "center", padding: "20px 0" }}>Loading...</div>
      ) : (
        <>
          <Toggle
            label="Private Profile"
            sublabel="Only friends can see your journal and stats"
            value={privateProfile}
            onChange={val => toggle("private_profile", val, setPrivateProfile)}
            disabled={saving}
          />
          <Toggle
            label="Private Check-ins by Default"
            sublabel="New check-ins will be private unless you change them"
            value={privateCheckins}
            onChange={val => toggle("default_private_checkins", val, setPrivateCheckins)}
            disabled={saving}
          />
        </>
      )}
      <div style={{ fontSize: type.xs, color: color.faint, marginTop: 16, lineHeight: 1.6 }}>
        Individual check-in visibility can always be changed from your journal.
      </div>
    </div>
  );
}

function GuideSection() {
  const [openSection, setOpenSection] = useState(null);

  const toggle = (id) => setOpenSection(prev => prev === id ? null : id);

  return (
    <div>
      <div style={{ fontSize: type.xs, color: color.dim, letterSpacing: 1, marginBottom: 16 }}>CIGAR GUIDE</div>

      <Section id="vitolas" title="Vitola size chart" openSection={openSection} onToggle={toggle}>
        <div style={{ fontSize: type.xs, color: color.dim, marginBottom: 10 }}>Size affects burn time, draw, and smoke temperature.</div>
        <Row label="Petit Corona" value='4–4.5" × 40–42 ring' sub="~20–30 min" />
        <Row label="Corona" value='5.5" × 42 ring' sub="~30–45 min" />
        <Row label="Robusto" value='4.75–5.5" × 48–52 ring' sub="~45–60 min" />
        <Row label="Toro" value='6" × 50–52 ring' sub="~60–75 min" />
        <Row label="Churchill" value='7" × 47–50 ring' sub="~60–90 min" />
        <Row label="Belicoso" value='5–5.5" × 50–52 ring' sub="Pointed head" />
        <Row label="Torpedo" value='6–7" × 52–54 ring' sub="Pointed head, wide body" />
        <Row label="Gordo/60 Ring" value='6" × 60 ring' sub="~75–90 min, very full" />
        <Row label="Lancero/Panetela" value='7+" × 38–40 ring' sub="Slim, long — complex draw" />
        <Row label="Perfecto" value="Varies" sub="Tapered at both ends" />
        <div style={{ fontSize: type.xs, color: color.faint, marginTop: 10 }}>Ring gauge = diameter in 64ths of an inch. A 52 ring = 52/64" wide.</div>
      </Section>

      <Section id="strength" title="Body & strength guide" openSection={openSection} onToggle={toggle}>
        <div style={{ fontSize: type.xs, color: color.dim, marginBottom: 10 }}>Strength = nicotine hit. Body = complexity and flavor intensity. They don't always match.</div>
        <Row label="Mild" value="Smooth, low nicotine" sub="Good for beginners" />
        <Row label="Mild-Medium" value="Easy draw with a bit more body" sub="Great step up from mild" />
        <Row label="Medium" value="Balanced flavor and nicotine" sub="Most popular range" />
        <Row label="Medium-Full" value="More complexity, noticeable nicotine" sub="For experienced smokers" />
        <Row label="Full" value="Bold, rich, strong nicotine hit" sub="Smoke after a meal" />
        <div style={{ fontSize: type.xs, color: color.tan, marginTop: 10, lineHeight: 1.6 }}>
          A cigar can be full-bodied (complex flavor) but medium strength (low nicotine) — like a Padron 1964. Don't confuse the two.
        </div>
      </Section>

      <Section id="wrappers" title="Wrapper types" openSection={openSection} onToggle={toggle}>
        <div style={{ fontSize: type.xs, color: color.dim, marginBottom: 10 }}>The wrapper leaf covers ~60% of what you taste.</div>
        <Row label="Claro" value="Light tan, mild and creamy" />
        <Row label="Colorado Claro" value="Medium brown, balanced" />
        <Row label="Colorado" value="Reddish-brown, full flavor" />
        <Row label="Colorado Maduro" value="Dark brown, rich and sweet" />
        <Row label="Maduro" value="Very dark, fermented longer — sweet, earthy, full" />
        <Row label="Oscuro" value="Almost black, strongest maduro" />
        <Row label="Natural" value="Light, slightly oily — Connecticut style" />
        <Row label="Candela" value="Green, very rare, grassy/sweet" />
        <div style={{ fontSize: type.xs, color: color.faint, marginTop: 10 }}>Common origins: Connecticut (mild), Ecuadorian Habano (spicy), Nicaraguan (bold), Cameroon (complex), San Andrés (maduro).</div>
      </Section>

      <Section id="origins" title="Origins guide" openSection={openSection} onToggle={toggle}>
        <Row label="Nicaragua" value="Spicy, complex, full-bodied. Jalapa and Estelí valleys." />
        <Row label="Dominican Republic" value="Smooth, creamy, medium body. Long tradition of quality." />
        <Row label="Honduras" value="Earthy, woody, medium-full. Often blended." />
        <Row label="Cuba" value="The original benchmark. Earthy, floral, nuanced. Limited availability." />
        <Row label="Ecuador" value="Known for wrapper leaves. Consistent humidity produces oily, even wrappers." />
        <Row label="Mexico" value="San Andrés maduro wrapper capital. Earthy and sweet." />
        <Row label="Peru" value="Emerging region. Similar profile to Nicaragua." />
        <Row label="Brazil" value="Mata Fina wrappers — oily, sweet, very dark." />
        <Row label="Cameroon" value="African wrapper leaf. Unique spice and wood notes." />
      </Section>

      <Section id="tasting" title="Tasting terms glossary" openSection={openSection} onToggle={toggle}>
        <Term word="Retrohale" def="Exhaling smoke through your nose to intensify flavor. Practice slowly — it's an acquired technique." />
        <Term word="Draw" def="The resistance when pulling air through the cigar. Should feel like sipping through a slightly restricted straw." />
        <Term word="Burn" def="How evenly the cigar burns. A good cigar burns evenly without needing constant touch-ups." />
        <Term word="Ash" def="Firm, white ash signals quality tobacco and a good burn. Flaky grey ash may indicate uneven aging." />
        <Term word="Pepper" def="A sharp spice — most common on the palate and retrohale in Nicaraguan cigars." />
        <Term word="Earthiness" def="A soil or forest floor note — common in full-bodied cigars." />
        <Term word="Creaminess" def="A smooth, buttery texture — often from Connecticut wrappers or Dominican tobacco." />
        <Term word="Sweetness" def="Natural sweetness from maduro wrappers or fermentation. Not artificial — more like cocoa or dried fruit." />
        <Term word="Complexity" def="How much the flavor changes as you smoke. A complex cigar evolves from first third to last." />
        <Term word="Transition" def="When flavor noticeably shifts — usually at the first and second third marks." />
        <Term word="Finish" def="The flavor that lingers after you exhale. A long, pleasant finish is a sign of quality." />
        <Term word="Plug" def="A blockage in the cigar that restricts draw. Sometimes resolved by gently squeezing the cigar." />
      </Section>

      <Section id="etiquette" title="Lounge & smoking etiquette" openSection={openSection} onToggle={toggle}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            "Never cut someone else's cigar without asking.",
            "Don't stub out a cigar like a cigarette — set it in the ashtray and let it go out naturally.",
            "Ash when it naturally falls or is long enough to drop. Don't tap obsessively.",
            "In a lounge, always ask before lighting up near others if in a non-smoking section.",
            "Don't blow smoke in anyone's direction.",
            "It's fine to relight a cigar within the first hour. After that, it may taste harsh.",
            "Don't comment negatively on someone else's cigar choice — taste is personal.",
          ].map((tip, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ color: color.goldLegacy, flexShrink: 0 }}>•</span>
              <span style={{ fontSize: 13, color: color.tan, lineHeight: 1.5 }}>{tip}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function HelpSection({ onReplayTour, user }) {
  const [type, setType] = useState("bug");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [pastFeedback, setPastFeedback] = useState([]);

  useEffect(() => {
    if (!user?.id) return;
    supabase.from("feedback").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(5)
      .then(({ data }) => setPastFeedback(data || []));
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);
    setError(null);

    // Get PostHog session ID if available
    let posthogSessionId = null;
    try {
      // eslint-disable-next-line no-undef
      posthogSessionId = window.posthog?.get_session_id?.() || null;
    } catch (e) {}

    const { error: dbError } = await supabase
      .from("feedback")
      .insert({
        user_id: user?.id || null,
        type,
        description: description.trim(),
        posthog_session_id: posthogSessionId,
      });

    setSubmitting(false);
    if (dbError) {
      setError("Something went wrong. Please try again.");
    } else {
      setSubmitted(true);
      setDescription("");
    }
  };

  return (
    <div>
      <div style={{ fontSize: type.xs, color: color.dim, letterSpacing: 1, marginBottom: 16 }}>REPORT A BUG / GIVE FEEDBACK</div>

      {submitted ? (
        <div style={{ background: `${color.green}22`, border: `1px solid ${color.green}55`, borderRadius: 10, padding: 20, textAlign: "center", marginBottom: 20 }}>
          <div style={{ marginBottom: 8, display: "flex", justifyContent: "center" }}><Icon.Check size={24} color={color.positive} /></div>
          <div style={{ fontSize: 14, color: color.green, fontWeight: 700, marginBottom: 4 }}>Thanks for the report!</div>
          <div style={{ fontSize: type.xs, color: color.faint }}>We'll look into it and follow up if needed.</div>
          <button onClick={() => setSubmitted(false)}
            style={{ marginTop: 14, background: "none", border: `1px solid ${color.lineStrong}`, borderRadius: 8, padding: "8px 16px", color: color.dim, fontSize: type.xs, cursor: "pointer", fontFamily: SANS }}>
            Submit Another
          </button>
        </div>
      ) : (
        <>
          {/* Type selector */}
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {[["bug", "Bug report"], ["feedback", "Feedback"]].map(([val, label]) => (
              <button key={val} onClick={() => setType(val)}
                style={{ flex: 1, background: type === val ? `${color.goldLegacy}22` : "none", border: `1px solid ${type === val ? color.goldLegacy : color.lineStrong}`, borderRadius: 8, padding: "10px 0", color: type === val ? color.goldLegacy : color.dim, fontSize: 13, fontWeight: type === val ? 700 : 400, cursor: "pointer", fontFamily: SANS }}>
                {label}
              </button>
            ))}
          </div>

          <div style={{ fontSize: type.xs, color: color.dim, letterSpacing: 1, marginBottom: 6 }}>
            {type === "bug" ? "DESCRIBE THE BUG — WHAT HAPPENED?" : "WHAT'S ON YOUR MIND?"}
          </div>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={type === "bug"
              ? "e.g. When I tap the like button on a check-in, the count doesn't update..."
              : "e.g. It would be great if I could filter my journal by brand..."}
            rows={5}
            style={{ width: "100%", background: color.surface, border: `1px solid ${color.lineStrong}`, borderRadius: 8, padding: "10px 12px", color: color.heading, fontSize: type.md, fontFamily: SANS, outline: "none", resize: "none", boxSizing: "border-box", marginBottom: 8 }}
          />
          {error && <div style={{ fontSize: type.xs, color: color.dangerText, marginBottom: 8 }}>{error}</div>}
          <div style={{ fontSize: type.xs, color: color.faint, marginBottom: 12 }}>
            {type === "bug" ? "Your session replay will be attached so we can see exactly what happened." : "Your feedback helps us build a better app."}
          </div>
          <button onClick={handleSubmit} disabled={submitting || !description.trim()}
            style={{ width: "100%", background: description.trim() ? color.gold : color.surfaceRaised, border: "none", borderRadius: 10, padding: 14, color: description.trim() ? color.bg : color.faint, fontSize: 14, fontWeight: 700, cursor: description.trim() ? "pointer" : "default", fontFamily: SANS }}>
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </>
      )}

      <div style={{ height: 1, background: color.line, margin: "24px 0" }} />

      {/* Past feedback with replies */}
      {pastFeedback.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: type.xs, color: color.dim, letterSpacing: 1, marginBottom: 12 }}>YOUR PREVIOUS SUBMISSIONS</div>
          {pastFeedback.map(item => (
            <div key={item.id} style={{ background: color.surface, border: `1px solid ${color.lineStrong}`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: type.xs, color: item.type === "bug" ? color.dangerText : color.green }}>
                  {item.type === "bug" ? "Bug report" : "Feedback"}
                </span>
                <span style={{ fontSize: type.xs, color: color.faint, marginLeft: "auto" }}>
                  {new Date(item.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </span>
              </div>
              <div style={{ fontSize: type.xs, color: color.tan, lineHeight: 1.5, marginBottom: item.reply_text ? 8 : 0 }}>
                {item.description}
              </div>
              {item.reply_text && (
                <div style={{ background: color.surfaceRaised, border: `1px solid ${color.green}33`, borderRadius: 8, padding: "8px 10px", marginTop: 8 }}>
                  <div style={{ fontSize: type.xs, color: color.green, letterSpacing: 1, marginBottom: 4 }}>REPLY FROM ASHED</div>
                  <div style={{ fontSize: type.xs, color: color.soft, lineHeight: 1.5 }}>{item.reply_text}</div>
                </div>
              )}
            </div>
          ))}
          <div style={{ height: 1, background: color.line, margin: "16px 0" }} />
        </div>
      )}

      <SectionLabel rule style={{ marginBottom: 4 }}>Help &amp; support</SectionLabel>

      {/* One hairline group. These were four identical outlined buttons,
          which gave a replay-the-tour action the same weight as a link to
          the terms of service. */}
      <Pressable onClick={onReplayTour} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${color.border}` }}>
        <span style={{ fontSize: type.md, color: color.textBody }}>Replay onboarding tour</span>
        <Icon.Chevron size={15} color={color.textFaint} />
      </Pressable>

      {[["Contact support", "mailto:support@ashed.app"],
        ["Privacy policy", "https://ashed.app/privacy"],
        ["Terms of service", "https://ashed.app/terms"]].map(([label, href]) => (
        <a key={label} href={href} target="_blank" rel="noreferrer"
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: TAP, textDecoration: "none", borderBottom: `1px solid ${color.border}` }}>
          <span style={{ fontSize: type.md, color: color.textBody }}>{label}</span>
          <Icon.Chevron size={15} color={color.textFaint} />
        </a>
      ))}

      <div style={{ marginTop: 24, textAlign: "center" }}>
        <div style={{ fontFamily: font.display, fontSize: type.md, fontWeight: weight.displayMed, color: color.textMuted, letterSpacing: "0.06em" }}>Ashed</div>
        <div style={{ fontFamily: font.mono, fontSize: type.xs, color: color.textFaint, marginTop: 4 }}>Version 0.9.2 (Alpha) · ashed.app</div>
      </div>
    </div>
  );
}